import { type ChapterItem, GetChapterRequest } from '@app/_proto/Protos/chapters';
import { ChaptersClient } from '@app/_proto/Protos/chapters.client';
import type { NovelItem } from '@app/_proto/Protos/novels';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import { usePrefetchGrpcApiWithQuery } from '@app/libs/api';
import { useAuth } from '@app/libs/auth';
import { useGrpcRequest } from '@app/libs/grpc';
import { useEventCallback } from '@app/libs/utils';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { InView } from 'react-intersection-observer';
import useConstant from 'use-constant';

type Props = {
    novel?: NovelItem;
    chapter?: ChapterItem;
};

export default function ChapterPrefetcher({ novel, chapter }: Props) {
    const { user } = useAuth();

    const novelSlug = chapter?.novelInfo?.slug;
    const nextChapter = chapter?.relatedChapterInfo?.nextChapter;
    const nextChapterSlug = nextChapter?.slug;

    const { isChapterLocked: isNextChapterLocked } = useChapterLockStatus({ novel, chapter: nextChapter });

    const getNextChapterRequest = useGrpcRequest(GetChapterRequest, {
        chapterProperty: {
            byProperty: {
                oneofKind: 'slugs',
                slugs: {
                    novelSlug: novelSlug ?? '',
                    chapterSlug: nextChapterSlug ?? '',
                },
            },
        },
    });

    const prefetchNextChapterQuery = usePrefetchGrpcApiWithQuery(
        ChaptersClient,
        c => c.getChapter,
        getNextChapterRequest,
        ['chapter', novelSlug, nextChapterSlug, user?.id],
        {
            staleTime: 1000 * 60 * 5, //Data stales in 5 minutes
        }
    );

    const shouldPrefetch = !isNextChapterLocked;

    const prefetchNextChapter = useEventCallback(async () => {
        if (shouldPrefetch) {
            try {
                await prefetchNextChapterQuery();
            } catch (e) {}
        }
    });

    const debouncedPrefetch = useConstant(() =>
        AwesomeDebouncePromise(prefetchNextChapter, 2000, {
            key: (chapterSlug: string) => chapterSlug,
            leading: true,
        })
    );

    const onInViewChange = (inView: boolean) => {
        if (inView && nextChapterSlug) {
            debouncedPrefetch();
        }
    };

    return (
        <InView as="div" onChange={onInViewChange}>
            <span></span>
        </InView>
    );
}
